import { supabase } from '../utils/supabase/client';
import tagServiceFirebase from './tagServiceFirebase';

class TagService {
  constructor() {
    this.storageKey = 'global_tags';
    this.useFirebase = true; // Tentar usar Firebase primeiro
    this.tagServiceFirebase = tagServiceFirebase; // Inicializar referência
  }

  // Função para remover tags duplicadas (por nome, case-insensitive) - REMOVIDA PARA EVITAR LOOP
  // removeDuplicateTags(tags) {
  //   if (!Array.isArray(tags)) return [];
  //   
  //   const seen = new Set();
  //   return tags.filter(tag => {
  //     // Criar uma chave única baseada apenas no nome (case-insensitive)
  //     const key = tag.name.toLowerCase();
  //     if (seen.has(key)) {
  //  //       return false; // Tag duplicada removida
  //     }
  //     seen.add(key);
  //     return true;
  //   });
  // }

  // Obter todas as tags globais
  async getAllTags() {
    try {
      // Tentar usar Firebase primeiro
      if (this.useFirebase) {
        try {
          const globalTags = await tagServiceFirebase.getAllGlobalTags();
          
          // Remover duplicatas de cada divisão
          const cleanedTags = {
            regiao: globalTags.regiao || [],
            material: globalTags.material || [],
            outros: globalTags.outros || [],
            tipoProduto: globalTags.tipoProduto || []
          };
          return cleanedTags;
        } catch (firebaseError) {
          this.useFirebase = false; // Desabilitar Firebase temporariamente
        }
      }

      // Fallback para localStorage
      const tags = localStorage.getItem(this.storageKey);
      if (tags) {
        const parsedTags = JSON.parse(tags);
        // Garantir que a estrutura está correta e remover duplicatas
        return {
          regiao: Array.isArray(parsedTags.regiao) ? parsedTags.regiao : [],
          material: Array.isArray(parsedTags.material) ? parsedTags.material : [],
          outros: Array.isArray(parsedTags.outros) ? parsedTags.outros : [],
          tipoProduto: Array.isArray(parsedTags.tipoProduto) ? parsedTags.tipoProduto : []
        };
      }
      
      return { regiao: [], material: [], outros: [], tipoProduto: [] };
    } catch (error) {
      return { regiao: [], material: [], outros: [], tipoProduto: [] };
    }
  }

  // Salvar todas as tags globais
  saveAllTags(tags) {
    try {
      localStorage.setItem(this.storageKey, JSON.stringify(tags));
      return true;
    } catch (error) {
      return false;
    }
  }

  // Adicionar uma nova tag global (sempre sem factoryId)
  async addTag(tag, factoryId = null) {
    try {
      // Verificar se a tag já existe antes de tentar criar
      const allTags = await this.getAllTags();
      const existingTag = allTags[tag.division].find(t => 
        t.name.toLowerCase() === tag.name.toLowerCase()
      );
      
      if (existingTag) {
        return { 
          success: false, 
          message: 'Tag já existe', 
          existingTag: existingTag 
        };
      }

      // Tentar usar Firebase primeiro (sempre sem factoryId para manter tags globais)
      if (this.useFirebase) {
        try {
          const result = await tagServiceFirebase.createTag(tag, null); // Sempre null para tags globais
          
          // Verificar se o Firebase retornou erro de duplicata
          if (result.success === false) {
            return result;
          }
          
          return { success: true, message: 'Tag adicionada com sucesso', id: result.id };
        } catch (firebaseError) {
          this.useFirebase = false; // Desabilitar Firebase temporariamente
        }
      }

      // Fallback para localStorage
      // Adicionar a nova tag
      allTags[tag.division].push(tag);
      
      // Salvar de volta
      this.saveAllTags(allTags);
      
      return { success: true, message: 'Tag adicionada com sucesso' };
    } catch (error) {
      return { success: false, message: 'Erro ao adicionar tag' };
    }
  }

  // Remover uma tag global
  async removeTag(tagId, division) {
    try {
      
      const allTags = await this.getAllTags();
      
      // Verificar se a divisão existe e é um array
      if (!allTags || !allTags[division] || !Array.isArray(allTags[division])) {
        return { success: false, message: 'Divisão não encontrada' };
      }
      
      allTags[division] = allTags[division].filter(tag => tag.id !== tagId);
      
      
      // Salvar localmente
      this.saveAllTags(allTags);
      
      // Tentar remover do Firebase também
      if (this.useFirebase) {
        try {
          await tagServiceFirebase.deleteTag(tagId);
          
          // Invalidar cache de tags após remoção
          try {
            const cacheService = await import('./cacheService');
            await cacheService.default.invalidate(`tags_global_${division}`, 'tags');
            await cacheService.default.invalidate(`tags_global_all`, 'tags');
          } catch (cacheError) {
          }
        } catch (firebaseError) {
          // Continuar mesmo se Firebase falhar
        }
      }
      
      // Remover também das fábricas que possuem essa tag
      this.removeTagFromAllFactories(tagId, division);
      
      // Forçar refresh das tags globais
      this.refreshGlobalTags();
      
      return { success: true, message: 'Tag removida com sucesso' };
    } catch (error) {
      return { success: false, message: 'Erro ao remover tag' };
    }
  }

  // Atualizar uma tag global
  updateTag(tagId, division, updatedTag) {
    try {
      const allTags = this.getAllTags();
      const tagIndex = allTags[division].findIndex(tag => tag.id === tagId);
      
      if (tagIndex === -1) {
        return { success: false, message: 'Tag não encontrada' };
      }

      allTags[division][tagIndex] = { ...updatedTag, id: tagId };
      this.saveAllTags(allTags);
      
      // Atualizar também nas fábricas que possuem essa tag
      this.updateTagInAllFactories(tagId, division, updatedTag);
      
      return { success: true, message: 'Tag atualizada com sucesso' };
    } catch (error) {
      return { success: false, message: 'Erro ao atualizar tag' };
    }
  }

  // Obter tags de uma fábrica específica
  async getFactoryTags(factoryId) {
    try {
      // Tentar usar Firebase primeiro
      if (this.useFirebase) {
        try {
          const tags = await tagServiceFirebase.getFactoryTags(factoryId);
          // Remover duplicatas de cada divisão
          const cleanedTags = {
            regiao: tags.regiao || [],
            material: tags.material || [],
            outros: tags.outros || [],
            tipoProduto: tags.tipoProduto || []
          };
          
          return cleanedTags;
        } catch (firebaseError) {
          this.useFirebase = false; // Desabilitar Firebase temporariamente
        }
      }

      // Fallback para localStorage
      const factoryTags = localStorage.getItem(`tags_${factoryId}`);
      if (factoryTags) {
        const parsedTags = JSON.parse(factoryTags);
        // Garantir que a estrutura está correta e remover duplicatas
        return {
          regiao: Array.isArray(parsedTags.regiao) ? parsedTags.regiao : [],
          material: Array.isArray(parsedTags.material) ? parsedTags.material : [],
          outros: Array.isArray(parsedTags.outros) ? parsedTags.outros : [],
          tipoProduto: Array.isArray(parsedTags.tipoProduto) ? parsedTags.tipoProduto : []
        };
      }
      
      return { regiao: [], material: [], outros: [], tipoProduto: [] };
    } catch (error) {
      return { regiao: [], material: [], outros: [], tipoProduto: [] };
    }
  }

  // Adicionar tag a uma fábrica específica
  async addTagToFactory(factoryId, tag) {
    try {
      
      // Tentar usar Firebase primeiro
      if (this.useFirebase) {
        try {
          const result = await tagServiceFirebase.createTag(tag, factoryId);
          return { success: true, message: 'Tag adicionada à fábrica', id: result.id };
        } catch (firebaseError) {
          this.useFirebase = false; // Desabilitar Firebase temporariamente
        }
      }

      // Fallback para localStorage
      const factoryTags = await this.getFactoryTags(factoryId);
      
      // Verificar se a tag já existe na fábrica
      const existingTag = factoryTags[tag.division].find(t => t.id === tag.id);
      if (existingTag) {
        return { success: false, message: 'Tag já existe nesta fábrica' };
      }

      // Adicionar a tag à fábrica
      factoryTags[tag.division].push(tag);
      
      // Salvar as tags da fábrica
      localStorage.setItem(`tags_${factoryId}`, JSON.stringify(factoryTags));
      
      return { success: true, message: 'Tag adicionada à fábrica' };
    } catch (error) {
      return { success: false, message: 'Erro ao adicionar tag à fábrica' };
    }
  }

  // Remover tag de uma fábrica específica
  async removeTagFromFactory(factoryId, tagId, division) {
    try {
      
      const factoryTags = await this.getFactoryTags(factoryId);
      
      // Verificar se a divisão existe e é um array
      if (!factoryTags || !factoryTags[division] || !Array.isArray(factoryTags[division])) {
        return { success: false, message: 'Divisão não encontrada na fábrica' };
      }
      
      factoryTags[division] = factoryTags[division].filter(tag => tag.id !== tagId);
      
      
      localStorage.setItem(`tags_${factoryId}`, JSON.stringify(factoryTags));
      
      return { success: true, message: 'Tag removida da fábrica' };
    } catch (error) {
      return { success: false, message: 'Erro ao remover tag da fábrica' };
    }
  }

  // Remover tag de todas as fábricas
  removeTagFromAllFactories(tagId, division) {
    try {
      // Obter todas as fábricas do localStorage
      const factories = JSON.parse(localStorage.getItem('factories') || '[]');
      
      factories.forEach(factory => {
        if (factory.id) {
          const factoryTags = this.getFactoryTags(factory.id);
          factoryTags[division] = factoryTags[division].filter(tag => tag.id !== tagId);
          localStorage.setItem(`tags_${factory.id}`, JSON.stringify(factoryTags));
        }
      });
    } catch (error) {
    }
  }

  // Atualizar tag em todas as fábricas
  updateTagInAllFactories(tagId, division, updatedTag) {
    try {
      // Obter todas as fábricas do localStorage
      const factories = JSON.parse(localStorage.getItem('factories') || '[]');
      
      factories.forEach(factory => {
        if (factory.id) {
          const factoryTags = this.getFactoryTags(factory.id);
          const tagIndex = factoryTags[division].findIndex(tag => tag.id === tagId);
          
          if (tagIndex !== -1) {
            factoryTags[division][tagIndex] = { ...updatedTag, id: tagId };
            localStorage.setItem(`tags_${factory.id}`, JSON.stringify(factoryTags));
          }
        }
      });
    } catch (error) {
    }
  }

  // Sincronizar tags globais com todas as fábricas
  syncGlobalTags() {
    try {
      const globalTags = this.getAllTags();
      
      // Obter todas as fábricas do localStorage
      const factories = JSON.parse(localStorage.getItem('factories') || '[]');
      
      factories.forEach(factory => {
        if (factory.id) {
          const factoryTags = this.getFactoryTags(factory.id);
          
          // Adicionar tags da fábrica ao global se não existirem
          Object.keys(factoryTags).forEach(division => {
            if (factoryTags[division]) {
              factoryTags[division].forEach(tag => {
                const existingTag = globalTags[division].find(t => t.name === tag.name);
                if (!existingTag) {
                  globalTags[division].push(tag);
                }
              });
            }
          });
        }
      });
      
      this.saveAllTags(globalTags);
    } catch (error) {
    }
  }

  // Obter tags disponíveis para uma fábrica (tags globais que não estão na fábrica)
  getAvailableTagsForFactory(factoryId) {
    try {
      const globalTags = this.getAllTags();
      const factoryTags = this.getFactoryTags(factoryId);
      
      const availableTags = {
        regiao: [],
        material: [],
        outros: []
      };
      
      Object.keys(globalTags).forEach(division => {
        availableTags[division] = globalTags[division].filter(globalTag => 
          !factoryTags[division].some(factoryTag => factoryTag.id === globalTag.id)
        );
      });
      
      return availableTags;
    } catch (error) {
      return { regiao: [], material: [], outros: [], tipoProduto: [] };
    }
  }

  // Testar conexão com Firebase
  async testConnection() {
    try {
      if (this.useFirebase) {
        const result = await tagServiceFirebase.testConnection();
        return result;
      }
      return { success: false, error: 'Firebase not enabled', details: 'useFirebase is false' };
    } catch (error) {
      return { success: false, error: 'Test failed', details: error.message };
    }
  }

  // Função para limpar tags duplicadas existentes
  async cleanupDuplicateTags() {
    try {
      
      const allTags = await this.getAllTags();
      let cleanedCount = 0;
      
      // Limpar duplicatas em cada divisão
      Object.keys(allTags).forEach(division => {
        const originalLength = allTags[division].length;
        // Manter tags como estão (sem remover duplicatas)
        // allTags[division] = this.removeDuplicateTags(allTags[division]);
        const cleanedLength = allTags[division].length;
        const removedCount = originalLength - cleanedLength;
        
        if (removedCount > 0) {
          cleanedCount += removedCount;
        }
      });
      
      if (cleanedCount > 0) {
        // Salvar tags limpas
        this.saveAllTags(allTags);
      } else {
      }
      
      return { success: true, cleanedCount };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  // Função para verificar se uma tag já existe (case-insensitive)
  async tagExists(tagName, division) {
    try {
      const allTags = await this.getAllTags();
      return allTags[division].some(tag => 
        tag.name.toLowerCase() === tagName.toLowerCase()
      );
    } catch (error) {
      return false;
    }
  }

  // Função para forçar refresh das tags globais
  async refreshGlobalTags() {
    try {
      
      // Limpar cache de tags globais
      try {
        const cacheService = await import('./cacheService');
        await cacheService.default.invalidate('tags_global_all', 'tags');
        await cacheService.default.invalidate('tags_global_regiao', 'tags');
        await cacheService.default.invalidate('tags_global_material', 'tags');
        await cacheService.default.invalidate('tags_global_outros', 'tags');
        await cacheService.default.invalidate('tags_global_tipoProduto', 'tags');
      } catch (cacheError) {
      }
      
      // Limpar localStorage também
      localStorage.removeItem(this.storageKey);
      
    } catch (error) {
    }
  }

  // Função para carregar tags diretamente do Firebase (sem fallback)
  async getAllTagsFromFirebase() {
    try {
      
      // Forçar uso do Firebase
      const originalUseFirebase = this.useFirebase;
      this.useFirebase = true;
      
      const globalTags = await tagServiceFirebase.getAllGlobalTags();
      
      // Remover duplicatas de cada divisão
      const cleanedTags = {
        regiao: globalTags.regiao || [],
        material: globalTags.material || [],
        outros: globalTags.outros || [],
        tipoProduto: globalTags.tipoProduto || []
      };
      
      
      // Restaurar estado original
      this.useFirebase = originalUseFirebase;
      
      return cleanedTags;
    } catch (error) {
      throw error; // Não fazer fallback, deixar o erro ser tratado pelo componente
    }
  }

  // Função para associar uma tag global existente a uma fábrica
  async associateTagToFactory(tagId, factoryId) {
    try {
      
      // Tentar usar Firebase primeiro
      if (this.useFirebase) {
        try {
          await tagServiceFirebase.associateTagToFactory(tagId, factoryId);
          return { success: true, message: 'Tag associada com sucesso' };
        } catch (firebaseError) {
          this.useFirebase = false; // Desabilitar Firebase temporariamente
        }
      }

      // Fallback para localStorage (apenas associar localmente)
      return { success: true, message: 'Tag associada localmente' };
    } catch (error) {
      return { success: false, message: 'Erro ao associar tag' };
    }
  }

  // Associar uma tag existente a uma fábrica (sistema híbrido Firebase + localStorage)
  async createTagAssociation(tag, factoryId) {
    try {
      const tagId = tag.id;
      if (!tagId) return { success: false, message: 'Tag sem ID' };

      const { error } = await supabase
        .from('factory_tag_associations')
        .upsert([{ factoryId, tagId }], { onConflict: 'factoryId,tagId' });

      if (error) throw error;

      this.saveAssociationToLocalStorage(tag, factoryId);
      return { success: true };
    } catch (error) {
      this.saveAssociationToLocalStorage(tag, factoryId);
      return { success: false, message: 'Erro ao criar associação da tag' };
    }
  }

  // Sincronizar associação (no-op — associações gerenciadas via factoryId na tag)
  async syncAssociationToFirebase(tag, factoryId) {
    return { success: true };
  }

  // Salvar associação no localStorage (cache local)
  saveAssociationToLocalStorage(tag, factoryId) {
    try {
      const associationKey = `factory_tags_${factoryId}`;
      let associations = JSON.parse(localStorage.getItem(associationKey) || '{}');
      
      // Garantir que a divisão existe
      if (!associations[tag.division]) {
        associations[tag.division] = [];
      }
      
      // Verificar se já está associada (por ID ou por nome+divisão)
      const alreadyAssociated = associations[tag.division].some(t => 
        (t.id && tag.id && t.id === tag.id) || 
        (t.name === tag.name && t.division === tag.division)
      );
      
      if (alreadyAssociated) {
        return { success: true, message: 'Tag já associada' };
      }
      
      // Adicionar associação local
      associations[tag.division].push({
        id: tag.id,
        name: tag.name,
        division: tag.division
      });
      
      // Salvar no localStorage
      localStorage.setItem(associationKey, JSON.stringify(associations));
      
      
      return { success: true, message: 'Tag associada localmente' };
    } catch (error) {
      return { success: false, message: 'Erro ao salvar localmente' };
    }
  }

  // Remover associação de uma tag de uma fábrica
  async removeTagAssociation(tag, factoryId) {
    try {
      const tagId = tag.id;
      if (!tagId) return { success: false, message: 'Tag sem ID' };

      const { error } = await supabase
        .from('factory_tag_associations')
        .delete()
        .eq('factoryId', factoryId)
        .eq('tagId', tagId);

      if (error) throw error;

      this.removeAssociationFromLocalStorage(tag, factoryId);
      return { success: true };
    } catch (error) {
      this.removeAssociationFromLocalStorage(tag, factoryId);
      return { success: false, message: 'Erro ao remover associação da tag' };
    }
  }

  // Remover associação do localStorage
  removeAssociationFromLocalStorage(tag, factoryId) {
    try {
      const associationKey = `factory_tags_${factoryId}`;
      let associations = JSON.parse(localStorage.getItem(associationKey) || '{}');
      
      // Garantir que a divisão existe
      if (!associations[tag.division]) {
        associations[tag.division] = [];
      }
      
      // Remover a tag da divisão (por ID ou por nome+divisão)
      associations[tag.division] = associations[tag.division].filter(t =>
        !((t.id && tag.id && t.id === tag.id) ||
          (t.name === tag.name && t.division === tag.division))
      );
      
      // Se a divisão ficou vazia, removê-la
      if (associations[tag.division].length === 0) {
        delete associations[tag.division];
      }
      
      // Salvar no localStorage
      localStorage.setItem(associationKey, JSON.stringify(associations));
      
      
      return { success: true, message: 'Associação removida localmente' };
    } catch (error) {
      return { success: false, message: 'Erro ao remover localmente' };
    }
  }

  // Remover associação do Firebase (backup)
  async removeAssociationFromFirebase(tag, factoryId) {
    try {
      const { data, error } = await supabase
        .from('tags')
        .select('*')
        .eq('type', 'association_backup')
        .eq('tagId', tag.id)
        .eq('factoryId', factoryId)
        .limit(1);

      if (error) {
        throw error;
      }

      const backupAssociation = data?.[0];
      if (!backupAssociation) {
        return { success: true };
      }

      const { error: deleteError } = await supabase
        .from('tags')
        .delete()
        .eq('id', backupAssociation.id);

      if (deleteError) {
        throw deleteError;
      }

      return { success: true };
    } catch (error) {
      throw error;
    }
  }

  // Carregar tags associadas a uma fábrica (localStorage principal + Firebase backup)
  async getFactoryTagsWithAssociations(factoryId) {
    try {
      const { data, error } = await supabase
        .from('factory_tag_associations')
        .select('tagId, tags(id, name, division)')
        .eq('factoryId', factoryId);

      if (error) throw error;

      const organized = { regiao: [], material: [], outros: [], tipoProduto: [] };
      (data || []).forEach(row => {
        const tag = row.tags;
        if (tag && tag.division && organized[tag.division]) {
          organized[tag.division].push({ id: tag.id, name: tag.name, division: tag.division });
        }
      });

      return organized;
    } catch (error) {
      return this.loadAssociationsFromLocalStorage(factoryId) ||
        { regiao: [], material: [], outros: [], tipoProduto: [] };
    }
  }

  // Forçar sincronização completa do Firebase para uma fábrica
  async forceSyncFromFirebase(factoryId) {
    try {
      
      const associationKey = `factory_tags_${factoryId}`;
      localStorage.removeItem(associationKey);
      
      const { data, error } = await supabase
        .from('tags')
        .select('*')
        .eq('type', 'association_backup')
        .eq('factoryId', factoryId);

      if (error) {
        throw error;
      }

      const backupAssociations = data || [];
      
      if (backupAssociations.length > 0) {
        const associations = {
          regiao: [],
          material: [],
          outros: [],
          tipoProduto: []
        };
        
        backupAssociations.forEach(backup => {
          
          if (associations[backup.tagDivision]) {
            associations[backup.tagDivision].push({
              id: backup.tagId,
              name: backup.tagName,
              division: backup.tagDivision,
              createdAt: backup.createdAt,
              updatedAt: backup.updatedAt
            });
          }
        });
        
        this.saveRestoredAssociationsToLocalStorage(factoryId, associations);
        
        return associations;
      }

      return {
        regiao: [],
        material: [],
        outros: [],
        tipoProduto: []
      };
    } catch (error) {
      
      if (error.message.includes('Failed to fetch') || error.message.includes('ERR_CONNECTION_REFUSED')) {
        return;
      }
      
      throw error;
    }
  }

  // Sincronizar com backup do Firebase (opcional)
  async syncFromFirebaseBackup(factoryId, localAssociations) {
    try {
      
      const { data, error } = await supabase
        .from('tags')
        .select('*')
        .eq('type', 'association_backup')
        .eq('factoryId', factoryId);

      if (error) {
        throw error;
      }

      const backupAssociations = data || [];
      
      if (backupAssociations.length > 0) {
        const hasLocalAssociations = Object.values(localAssociations).some(division => division.length > 0);
        const totalLocalAssociations = Object.values(localAssociations).reduce((sum, division) => sum + division.length, 0);

        
        if (!hasLocalAssociations || totalLocalAssociations < backupAssociations.length) {
          
          Object.keys(localAssociations).forEach(division => {
            localAssociations[division] = [];
          });
          
          backupAssociations.forEach(backup => {
            
            if (localAssociations[backup.tagDivision]) {
              localAssociations[backup.tagDivision].push({
                id: backup.tagId,
                name: backup.tagName,
                division: backup.tagDivision,
                createdAt: backup.createdAt,
                updatedAt: backup.updatedAt
              });
            }
          });
          
          this.saveRestoredAssociationsToLocalStorage(factoryId, localAssociations);
        } else {
        }
      } else {
      }
    } catch (error) {
      
      if (error.message.includes('Failed to fetch') || error.message.includes('ERR_CONNECTION_REFUSED')) {
        return;
      }
      
      throw error;
    }
  }

  // Salvar associações restauradas no localStorage
  saveRestoredAssociationsToLocalStorage(factoryId, associations) {
    try {
      const associationKey = `factory_tags_${factoryId}`;
      const localAssociations = {};
      
      Object.keys(associations).forEach(division => {
        if (associations[division].length > 0) {
          localAssociations[division] = associations[division].map(tag => ({
            id: tag.id,
            name: tag.name,
            division: tag.division
          }));
        }
      });
      
      localStorage.setItem(associationKey, JSON.stringify(localAssociations));
    } catch (error) {
    }
  }

  // Carregar associações do localStorage (sistema principal)
  loadAssociationsFromLocalStorage(factoryId) {
    try {
      const associationKey = `factory_tags_${factoryId}`;
      const associations = JSON.parse(localStorage.getItem(associationKey) || '{}');
      
      
      // Organizar tags por divisão
      const result = {
        regiao: [],
        material: [],
        outros: [],
        tipoProduto: []
      };
      
      // Para cada divisão, adicionar as tags associadas
      Object.keys(result).forEach(division => {
        if (associations[division]) {
          result[division] = associations[division].map(assoc => ({
            id: assoc.id,
            name: assoc.name,
            division: assoc.division,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString()
          }));
        }
      });
      
      return result;
    } catch (error) {
      return {
        regiao: [],
        material: [],
        outros: [],
        tipoProduto: []
      };
    }
  }

}

const tagService = new TagService();

export default tagService;
